import React, { FC } from 'react'
import { GetServerSideProps } from 'next'
import { NewsArticle, VideoObject } from 'schema-dts'
import {
    AdPlacementType,
    AdProps,
    ArticleSectionProps,
    ArticleSectionType,
    ComponentType,
    LayoutProps,
} from '@sport1/types/web'
import { getRecommendedVideos } from '../api/cms/video/[id]'
import getArticleNewsArticle from '@/components/LinkedData/getArticleNewsArticle'
import getArticleVideoObjects from '@/components/LinkedData/getArticleVideoObjects'
import StandardLayout from '@/layouts/Standard'
import PageContentLayout from '@/layouts/Standard/PageContentLayout'
import { getTagBySlug } from '@/pages/api/cms/content/tag/[id]'
import { getSport1Navigation } from '@/pages/api/cms/navigation'
import { findValueByKey } from '@/utils/meta/findValue'
import { NavigationData } from '@/utils/navigation/Standard/types/NavigationItem'
import { enrichSimpleLayout, getSimpleLayout } from '@/pages/api/cms/content/urlalias/[...slug]'
import { addAdsToLayout, AdInfoProps, AdPlatform } from '@/helpers/adHelper'
import handleRejection, { PageNotFound } from '@/utils/errors/handleRejection'
import setConsentNeeded from '@/utils/meta/setConsentNeeded'
import { ArticleTopVideoExtendedProps, Video } from '@/types/video'
import extendVideos from '@/utils/video/extendVideos'

type Props = {
    deeplink?: string
    layoutData: LayoutProps
    linkedData?: Array<NewsArticle | VideoObject | null>
    navigation?: NavigationData
    showArticleToolbar?: boolean
}

const Articles: FC<Props> = ({ layoutData, navigation, showArticleToolbar }) => {
    const isAdsActive = layoutData.meta?.ad?.adsEnabledForLayout
    return (
        <StandardLayout
            navigation={navigation}
            isAdsActive={isAdsActive}
            showArticleToolbar={showArticleToolbar}
            recommendedArticles={layoutData.bottomStickyComponents}
        >
            <PageContentLayout layoutData={layoutData} isAdsActive={isAdsActive} />
        </StandardLayout>
    )
}

/**
 * Testing for both, slugFromLayout with or without /news prefix
 */
const shouldRedirectElsewhere = (
    slugFromLayout: string | undefined,
    slugFromQuery: string
): string | undefined => {
    /**
     * These IFs can be terser. But I want to be explicit about the
     * conditions.
     */
    if (slugFromLayout) {
        if (slugFromLayout.startsWith('/news')) {
            if (slugFromLayout !== `/news/${slugFromQuery}`) {
                return slugFromLayout
            }
        } else {
            if (slugFromLayout !== `/${slugFromQuery}`) {
                return `/news${slugFromLayout}`
            }
        }
    }

    return undefined
}

export const getServerSideProps: GetServerSideProps<Props | PageNotFound> = async ({
    query,
    res,
}) => {
    const querySlug = query.slug as string[]
    const slug = querySlug.join('/')

    let navigationData
    let layout
    try {
        ;[navigationData, layout] = await Promise.all([
            getSport1Navigation(),
            getSimpleLayout(slug),
        ])
    } catch (error) {
        return handleRejection(error, { slug })
    }
    if (layout?.redirectUrl) {
        return {
            redirect: {
                destination: layout?.redirectUrl,
                permanent: false,
            },
            props: {},
        }
    }

    const articleID = findValueByKey(layout.meta?.tracking, 'page_content_id')
    const pageTitle = findValueByKey(layout.meta?.tracking, 'page_title')

    const slugFromLayout = findValueByKey(layout.meta?.tracking, 'page_slug')
    const slugNew = shouldRedirectElsewhere(slugFromLayout, slug)
    if (slugNew) {
        res.writeHead(301, { Location: slugNew })
        res.end()
        return { props: { layoutData: layout } }
    }

    layout = await enrichSimpleLayout(layout)

    let tag
    if (query.slug && isNaN(Number(query.slug[0]))) {
        // slugs like this are tricky:
        // /fussball/2021/05/dfl-boss-christian-seifert-erhoeht-druck-auf-dfb-vize-rainer-koch
        let tagSlug = `/${slug}`.match(new RegExp('.+?(?=/[0-9]{4})'))
        tagSlug = tagSlug || `/${slug}`.match(new RegExp('.+?(?=/[a-z])'))
        if (tagSlug) {
            tag = await getTagBySlug(tagSlug[0]).catch(() => null)
            if (tag) {
                navigationData = { ...navigationData, tag }
            }
        }
    }

    const ads: AdInfoProps[] = [
        {
            placement: AdPlacementType.SKYSCRAPER,
            platform: AdPlatform.WEB,
            position: 0,
        },
    ]

    addAdsToLayout({
        adInfos: ads,
        layout,
    })

    /**
     * TODO: Ströer
     * Move banner out of article's text as long as the API does not
     * serve it this way.
     */
    const articleSection = layout.components[0] as ArticleSectionProps | undefined
    if (articleSection?.content) {
        let firstBanner: AdProps | undefined
        articleSection.content = articleSection.content.filter(content => {
            if (firstBanner) {
                return true
            }
            if (content.type !== 'AD' || content.ad.placement !== 'BANNER') {
                return true
            }
            firstBanner = content as unknown as AdProps
            return false
        })
        if (firstBanner) {
            layout.components.unshift(firstBanner)
        }
    }

    if (
        slug === 'unternehmen/nutzungsbedingungen' ||
        slug === 'unternehmen/2023/10/bereitstellungsvertrag'
    ) {
        layout = setConsentNeeded({ layout, consentNeeded: false })
    }

    let articleTopVideo: ArticleTopVideoExtendedProps | undefined
    layout.components.forEach(item => {
        if (item.type === ComponentType.ARTICLE_SECTION) {
            articleTopVideo = item.content.find(
                articleItem => articleItem.type === ArticleSectionType.TOP_VIDEO
            ) as ArticleTopVideoExtendedProps
        }
    })

    if (articleTopVideo && articleTopVideo.recommendedVideosUrl) {
        articleTopVideo['recommendedVideos'] = await getRecommendedVideos(
            `${articleTopVideo.recommendedVideosUrl}/extended/true`,
            articleTopVideo.id
        )
    }

    if (articleTopVideo?.recommendedVideos?.length) {
        await extendVideos(articleTopVideo.recommendedVideos as unknown as Video[])
    }

    const hasBottomComponents =
        layout?.bottomStickyComponents &&
        Array.isArray(layout.bottomStickyComponents) &&
        layout.bottomStickyComponents.length > 0

    if (hasBottomComponents) {
        layout.bottomStickyComponents = [...layout.bottomStickyComponents!].filter(
            item => item?.slug !== `/news/${slug}`
        )
    }
    let showArticleToolbar = true
    if (slug.startsWith('sportwetten/') || slug.startsWith('casino/')) {
        showArticleToolbar = false
    }

    if (articleID) {
        const deeplink = `sport1://article/${articleID}`
        res.setHeader('deeplink', deeplink)
        return {
            props: {
                showArticleToolbar,
                deeplink,
                layoutData: layout,
                linkedData: [
                    getArticleNewsArticle({ layoutProps: layout }),
                    ...getArticleVideoObjects({ layoutProps: layout }),
                ],
                navigation: { ...navigationData, breadCrumbs: { pageTitle } },
            },
        }
    }
    return {
        props: {
            showArticleToolbar,
            layoutData: layout,
            navigation: { ...navigationData, breadCrumbs: { pageTitle } },
        },
    }
}

export default Articles
